<script setup>
import { onMounted, onUnmounted, ref } from "vue";
import { getDashboardInfo, getLogs } from "@/services/dashboardService";
import socket from "@/services/socket";
import { formatDate } from "@/globals/utils/formatDate";
import TimeAgo from "@/components/utils/TimeAgo.vue";
import Chart from "chart.js/auto";

// Data
const dashboardInfo = ref({});
const stats = ref([
  { label: "Total Users", value: 0, icon: "group" },
  { label: "Cars", value: 0, icon: "electric_car" },
  { label: "Monthly Revenue", value: "$0", icon: "attach_money" },
  { label: "Pending Trips", value: 0, icon: "pending" },
]);
const recentActivities = ref([]);

// Chart instance
let chartInstance = null;

// Fetch dashboard data
const fetchDashboardInfo = async () => {
  try {
    const data = await getDashboardInfo();
    dashboardInfo.value = data;
    stats.value[0].value = data.totalUsers;
    stats.value[1].value = data.totalCars;
    stats.value[2].value = `$${data.amountThisMonth}`;
    stats.value[3].value = data.pendingRides;
    updateChart();
  } catch (error) {
    console.error("Error fetching dashboard info:", error);
  }
};

// Fetch logs
const fetchLogs = async () => {
  try {
    const logs = await getLogs();
    recentActivities.value = logs.map(log => ({
      id: log._id,
      user: log.user.name,
      message: log.description.replace(/_/g, ' '), // Replace underscores with spaces
      timestamp: log.createdAt,
      action: log.action,
    }));
  } catch (error) {
    console.error("Error fetching logs:", error);
  }
};

// Render or update the chart
const updateChart = () => {
  const ctx = document.getElementById("statsChart").getContext("2d");

  // Destroy the previous chart instance if it exists
  if (chartInstance) {
    chartInstance.destroy();
  }

  chartInstance = new Chart(ctx, {
    type: "bar",
    data: {
      labels: ["Total Users", "Active Trips", "Pending Trips", "Completed Trips"],
      datasets: [
        {
          label: "Statistics",
          data: [
            dashboardInfo.value.totalUsers || 0,
            dashboardInfo.value.inProgressRides || 0,
            dashboardInfo.value.pendingRides || 0,
            dashboardInfo.value.completedRides || 0,
          ],
          backgroundColor: ["#2563EB", "#10B981", "#F59E0B", "#EF4444"],
          borderColor: ["#1D4ED8", "#059669", "#D97706", "#B91C1C"],
          borderWidth: 1,
          barThickness: 30,
          hoverBackgroundColor: ["#1E40AF", "#047857", "#C2410C", "#9A3412"],
        },
      ],
    },
    options: {
      responsive: true,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
        },
      },
      scales: {
        x: {
          grid: { display: false },
        },
        y: {
          grid: { color: "#E5E7EB" },
          ticks: { beginAtZero: true },
        },
      },
    },
  });
};

// Initialize dashboard
const initDashboard = async () => {
  await fetchDashboardInfo();
  await fetchLogs();
};

// Socket event handling
const setupSocketListeners = () => {
  socket.on("newLogin", initDashboard);
  socket.on("newRide", initDashboard);
  socket.on("newLogout", initDashboard);
  socket.on("rideStatusUpdated", initDashboard);
  socket.on("rideCancelled", initDashboard);
};

const removeSocketListeners = () => {
  socket.off("newLogin");
  socket.off("newRide");
  socket.off("newLogout");
  socket.off("rideStatusUpdated");
  socket.off("rideCancelled");
};

// Lifecycle hooks
onMounted(async () => {
  await initDashboard();
  socket.connect();
  setupSocketListeners();
  console.log("Socket connected:", socket.connected);
});

onUnmounted(() => {
  removeSocketListeners();
  socket.disconnect();
  if (chartInstance) chartInstance.destroy(); // Destroy chart on unmount
});
</script>

<template>
  <div class="p-6 bg-gray-100 dark:bg-gray-800 min-h-screen">
    <div class="container mx-auto">
      <!-- Header -->
      <header class="mb-6 text-center">
        <h1 class="text-3xl font-bold text-gray-800 dark:text-gray-400">Admin Dashboard</h1>
        <p class="text-gray-600 dark:text-gray-500">Overview of the platform's performance</p>
      </header>

      <!-- Statistics Cards -->
      <section class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div
            v-for="stat in stats"
            :key="stat.label"
            class="p-6 bg-white dark:bg-gray-700 rounded shadow-md flex items-center space-x-4"
        >
          <div class="flex items-center justify-center bg-blue-100 dark:bg-gray-400 rounded-full w-12 h-12">
            <span class="material-icons">{{ stat.icon }}</span>
          </div>
          <div>
            <h3 class="text-xl font-semibold text-gray-800 dark:text-gray-300">{{ stat.value }}</h3>
            <p class="text-gray-600 dark:text-gray-400">{{ stat.label }}</p>
          </div>
        </div>
      </section>

      <!-- Recent Activities -->
      <section>
        <h2 class="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">Recent Activities</h2>
        <div class="bg-white dark:bg-gray-700 rounded shadow-md p-6">
          <ul>
            <li
                v-for="activity in recentActivities"
                :key="activity.id"
                class="flex justify-between dark:border-gray-500 py-3 border-b last:border-none"
            >
              <p class="text-gray-700 dark:text-gray-200">
                <span class="bg-blue-300 dark:bg-gray-600 text-blue-950 dark:text-gray-300 rounded border-blue-950 font-semibold py-1 px-2 mr-2">
                  {{ activity.action }}
                </span>
                <strong>{{ activity.user }}</strong> {{ activity.message }}
              </p>
              <TimeAgo :date="activity.timestamp" :title="formatDate(activity.timestamp, 'DD/MM/YYYY HH:mm')" />
            </li>
          </ul>
        </div>
      </section>

      <!-- Chart Section -->
      <section>
        <h2 class="text-xl font-semibold text-gray-800 mt-8 mb-4">Platform Statistics</h2>
        <div class="bg-white dark:bg-gray-700 rounded shadow-md p-6">
          <canvas id="statsChart" class="w-full h-64"></canvas>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
/* Additional styles for better visuals */
</style>
